import React from 'react';
import { Badge } from '@parsina/framework';

import { useTranslation } from '../i18n';
import {
  STATUS_VARIANT, UNPROVISIONED, TIER_VARIANT, statusLabelKey, tierLabelKey,
} from '../constants/subscription';
import { planLabel } from '../lib/format';

/**
 * A clinic's subscription STATE, as one badge.
 *
 * «No subscription» is its own state, never dressed up as a status. A clinic with
 * no row is reported by the subscription service as entitled — it fails open — so
 * rendering it as ACTIVE hides the clinics nobody ever provisioned, and rendering
 * it as EXPIRED sends an operator to renew something that was never sold. Both
 * mistakes are invisible in a screenshot, which is why this decision lives in one
 * component rather than at three call sites.
 */
export function SubscriptionBadge({ clinic, status, className }) {
  const { t } = useTranslation();
  const value = clinic
    ? (clinic.hasSubscription ? clinic.subscriptionStatus : UNPROVISIONED)
    : (status || UNPROVISIONED);

  return (
    <Badge variant={STATUS_VARIANT[value] || 'outline'} className={className}>
      {t(statusLabelKey(value))}
    </Badge>
  );
}

/**
 * A clinic's capability TIER — the other axis, and the pair is the whole point.
 *
 * Status says whether a clinic is in good standing; tier says what it bought. A
 * پایه clinic is ACTIVE and earns nothing; a lapsed طلایی clinic is EXPIRED and
 * still counts as طلایی, because it keeps the tier it paid for. Reading either as
 * the other is what the split exists to prevent.
 */
export function TierBadge({ clinic, className }) {
  const { t } = useTranslation();
  if (!clinic?.hasSubscription) return null;
  const known = tierLabelKey(clinic.capabilitySet);
  return (
    <Badge
      // An unrecognised tier gets the neutral outline rather than a colour that
      // would imply we know where it sits on the ladder.
      variant={(known && TIER_VARIANT[clinic.capabilitySet]) || 'outline'}
      className={className}
    >
      {planLabel(clinic, t)}
    </Badge>
  );
}

export default SubscriptionBadge;
